import type { TokenInfo } from '@renec-foundation/rpl-token-registry'
import { PublicKey } from '@solana/web3.js'

import {
  RENEC_MINT_ADDRESS,
  RENGN_MINT_ADDRESS,
  REUSD_MINT_ADDRESS,
  REVND_MINT_ADDRESS,
} from '@/hooks/use-tokens'

export const FIAT_TOKENS = [REVND_MINT_ADDRESS, RENGN_MINT_ADDRESS]

/**
 * Returns the ordered token pair `[base, quote]` (`1 base = X quote`) to be
 * used for rate displaying purposes.
 */
export const orderTokensByUiQuote = (
  defaultOrder: [PublicKey, PublicKey],
  currentPair = defaultOrder,
): [PublicKey, PublicKey] => {
  if (defaultOrder.length !== 2 || defaultOrder[0].equals(defaultOrder[1])) {
    throw new Error('Invalid default order pair.')
  }

  if (
    currentPair[0].equals(currentPair[1]) ||
    currentPair.filter(
      (mint) => mint.equals(defaultOrder[0]) || mint.equals(defaultOrder[1]),
    ).length !== 2
  ) {
    throw new Error('Invalid current pair.')
  }

  const [token0, token1] = currentPair
  const isToken0Fiat = FIAT_TOKENS.includes(token0.toBase58())
  const isToken1Fiat = FIAT_TOKENS.includes(token1.toBase58())

  if (isToken0Fiat && isToken1Fiat) {
    return defaultOrder
  }

  if (isToken0Fiat) {
    return [token1, token0]
  }

  if (isToken1Fiat) {
    return [token0, token1]
  }

  if (token0.toBase58() === REUSD_MINT_ADDRESS) {
    return [token1, token0]
  }

  if (token1.toBase58() === REUSD_MINT_ADDRESS) {
    return [token0, token1]
  }

  if (token0.toBase58() === RENEC_MINT_ADDRESS) {
    return [token1, token0]
  }

  if (token1.toBase58() === RENEC_MINT_ADDRESS) {
    return [token0, token1]
  }

  return defaultOrder
}

/**
 * Wrapper of {@link orderTokensByUiQuote} for {@link TokenInfo}.
 */
export const orderTokenInfosByUiQuote = <T extends TokenInfo>(
  defaultOrder: [T, T],
  currentPair = defaultOrder,
): [T, T] => {
  const [token0] = orderTokensByUiQuote(
    [new PublicKey(defaultOrder[0].address), new PublicKey(defaultOrder[1].address)],
    [new PublicKey(currentPair[0].address), new PublicKey(currentPair[1].address)],
  )

  const isFirstToken0 = currentPair[0].address === token0.toBase58()
  return [
    isFirstToken0 ? currentPair[0] : currentPair[1],
    isFirstToken0 ? currentPair[1] : currentPair[0],
  ]
}
